import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Route from '../src/layout/Route';
import LoginEntryPage from './LoginEntryPage';
import { getLoginRedirectOptionsForIdentifier, getSsoLoginOptions } from './js/auth/ssoRouting';

function App() {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [silentAuthStatus, setSilentAuthStatus] = useState('idle');
  const silentAuthAttempted = useRef(false);

  useEffect(() => {
    if (isLoading || isAuthenticated || silentAuthAttempted.current) {
      return;
    }
    silentAuthAttempted.current = true;
    setSilentAuthStatus('trying');
    getAccessTokenSilently()
      .catch((err) => {
        console.warn('[silent-auth] silent authentication failed', err);
        if (err?.error === 'consent_required') {
          loginWithRedirect().catch(() => setSilentAuthStatus('failed'));
        } else {
          setSilentAuthStatus('failed');
        }
      });
  }, [isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (isAuthenticated) {
      silentAuthAttempted.current = false;
      setSilentAuthStatus('idle');
    }
  }, [isAuthenticated]);

  const handleLogin = async (identifier) => {
    const trimmedIdentifier = String(identifier || '').trim();
    const isEmail = trimmedIdentifier.includes('@');

    // Tracks whether check-sso actually answered, vs. couldn't be reached at
    // all — distinct from `ssoRequired: false`, which is the server's
    // authoritative "no". Used below to pick an honest message.
    let checkResult = null;
    let checkSsoUnreachable = false;

    // ONLY the fetch lives in this try. Every decision is made after it, so a
    // "blocked" Error thrown below cannot be swallowed by this catch and
    // misreported as a network failure.
    try {
      const res = await fetch(`/api/auth/check-sso?username=${encodeURIComponent(trimmedIdentifier)}`);
      if (res.ok) {
        checkResult = await res.json();
      } else {
        checkSsoUnreachable = true;
      }
    } catch {
      checkSsoUnreachable = true;
    }

    // Blocked cases throw. LoginEntryPage's handleSubmit catches and renders
    // the message inline above the input, so the form stays on screen and
    // there is nothing to go "back" from.
    if (checkResult && checkResult.ssoRequired) {
      if (isEmail) {
        // A tenant configured via Auth0 Organizations has no `connection`
        // (only `organization`) — check-sso returns whichever one applies.
        const authorizationParams = { login_hint: trimmedIdentifier };
        if (checkResult.connection) authorizationParams.connection = checkResult.connection;
        if (checkResult.organization) authorizationParams.organization = checkResult.organization;
        await loginWithRedirect({ authorizationParams });
        return;
      }
      // An SSO user typed their username — ask for the company email instead.
      throw new Error(
        'Your company has enabled single sign-on (SSO). Please enter your company email address to login.'
      );
    }

    const { options } = trimmedIdentifier
      ? getLoginRedirectOptionsForIdentifier(trimmedIdentifier)
      : getSsoLoginOptions();

    if (isEmail) {
      // The client holds no domain list any more, so every email is "deferred"
      // (see ssoRouting.js) and check-sso is the only authority. A res.ok with
      // ssoRequired:false is its authoritative "no"; anything else reaching
      // here means it never answered, so say that rather than the flatly wrong
      // "your company hasn't enabled SSO".
      if (checkSsoUnreachable) {
        throw new Error(
          "We couldn't verify your company's SSO status right now. Please check your connection and try again, or enter your SKYPAD username."
        );
      }
      throw new Error(
        'Your company has not enabled single sign-on (SSO). Please enter your SKYPAD username.'
      );
    }

    await loginWithRedirect({ authorizationParams: options });
  };

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    if (silentAuthStatus !== 'failed') {
      return null;
    }
    return (
      <LoginEntryPage onSubmit={handleLogin} />
    );
  }

  return <Route />;
}

export default App;
